import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';

/**
 * Table pagination controls, including page navigation and rows per page selection.
 *
 * @param {Object} props
 * @param {number} props.currentPage - The current active page number
 * @param {Function} props.onPageChange - Callback function when page number changes
 * @param {Function} props.onRowsPerPageChange - Callback function when rows per page selection changes
 * @param {string|number} props.rowsPerPage - Current number of rows per page
 * @param {number} props.selectedRows - Number of currently selected rows
 * @param {number} props.totalRows - Total number of rows in the dataset
 * @param {number} props.totalPages - Total number of available pages
 * @returns {React.ReactElement} A pagination controls component with page navigation and row selection display
 */
function TablePageControls({
    currentPage,
    onPageChange,
    onRowsPerPageChange,
    rowsPerPage,
    selectedRows,
    totalPages,
    totalRows,
}) {
    return (
        <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
                <p className="text-muted-foreground">
                    {selectedRows} of {totalRows} row(s) selected
                </p>
            </div>
            <div className="flex items-center gap-10">
                <div className="flex items-center gap-2">
                    <p className="text-sm">Rows per page</p>
                    <Select value={rowsPerPage} onValueChange={onRowsPerPageChange}>
                        <SelectTrigger size="sm">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="5">5</SelectItem>
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="20">20</SelectItem>
                            <SelectItem value="30">30</SelectItem>
                            <SelectItem value="all">All</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex items-center gap-2">
                    <p className="text-sm">
                        Page {currentPage} of {totalPages}
                    </p>
                    <div className="flex gap-1">
                        <Button
                            variant="outline"
                            size="iconSm"
                            onClick={() => onPageChange(1)}
                            disabled={currentPage === 1}
                            type="button"
                        >
                            <ChevronFirst className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="iconSm"
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            type="button"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="iconSm"
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            type="button"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="iconSm"
                            onClick={() => onPageChange(totalPages)}
                            disabled={currentPage === totalPages}
                            type="button"
                        >
                            <ChevronLast className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TablePageControls;
